import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Loader } from '@mantine/core';
import styles from '../../../styles/logpost.module.css';
import AddIcon from '../../../assets/icons/AddIcon';
import { Button } from '../../styledComponents';
import LogPosts from './LogPosts';
import CustomDatePicker from '../customDatePicker/CustomDatePicker';
import { getLogPostList } from '../../../services/inbound.service';
import LogPostFtux from './LogPostFtux';
import AddLogPost from './AddLogPost';
import LoaderSvg from '../../../assets/images/loader.svg';
import LoadMoreButton from './LoadMoreButton';
import { getCreatorDetails } from '../../../services/dashboard.service';
import { LogPostData } from './types';
import { reverseLogPosts } from '../../../utils/global';

type DateOrNull = Date | null;
type DateRangeArray = [DateOrNull, DateOrNull];

// const getNexCursor = (data: Record<string, string>) => {
//   const resultArray: any[] = Object.values(data);
//   const lastArr: any[] = resultArray?.[resultArray.length - 1];
//   const lastPost = lastArr?.[lastArr.length - 1];
//   if (lastPost?.logPostId) {
//     return lastPost?.logPostId;
//   }
//   return null;
// };

interface Props {
  dateRange: DateRangeArray;
  setDateRange: React.Dispatch<React.SetStateAction<DateRangeArray>>;
  selectedDays: number;
  setSelectedDays: React.Dispatch<React.SetStateAction<number>>;
}

function LogPostsContainer({ dateRange, setDateRange, selectedDays, setSelectedDays }: Props) {
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [isAddLogPostOpen, setIsAddLogPostOpen] = useState(false);
  const [creatorDetails, setCreatorDetails] = useState<Record<any, any>>({});
  const memoizedDateRange = useRef<any[]>([]);
  const [logPostData, setLogPostData] = useState<LogPostData>({});
  const [loadingLogPostData, setLoadingLogPostData] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalLogPostCount, setTotalLogPostCount] = useState({ current: 0, total: 0 });
  const nextCreatedAt = useRef<string | null>(null);

  const getLogPostData = useCallback(
    async ({ newDateRange = dateRange }) => {
      setLoadingLogPostData(true);
      const creatorRes = await getCreatorDetails({});
      if (!creatorRes?.status) return;
      const newCreatorDetails = creatorRes.data.creatorDetails;
      setCreatorDetails(newCreatorDetails);
      let body: object = {
        influencerId: newCreatorDetails._id,
        createdAt: null,
      };
      if (newDateRange[0] && newDateRange[1]) {
        body = { ...body, startDate: newDateRange[0], endDate: newDateRange[1] };
      }
      const res = await getLogPostList(body);
      if (res.status) {
        setLogPostData(reverseLogPosts(res.data.logPosts) || {});
        setTotalLogPostCount({
          current: Object.values(res.data.logPosts).reduce<number>((p, c: any) => {
            const newCount = p + c.length;
            return newCount;
          }, 0),
          total: res.data.totalLogPostInTimePeriod,
        });

        nextCreatedAt.current = res.data.lastCreatedAt;
      } else {
        toast.error('Error getting Log Post data.');
      }
      setLoadingLogPostData(false);
    },
    [dateRange]
  );

  useEffect(() => {
    getLogPostData({ newDateRange: dateRange });
  }, [dateRange, getLogPostData]);

  const loadMoreLogPost = async () => {
    if (!nextCreatedAt.current) return;
    setLoadingMore(true);
    let body: object = {
      influencerId: creatorDetails._id,
      createdAt: nextCreatedAt.current,
    };
    if (dateRange[0] && dateRange[1]) {
      body = { ...body, startDate: dateRange[0], endDate: dateRange[1] };
    }
    const res = await getLogPostList(body);
    if (res.status) {
      const updateData = { ...logPostData };
      Object.entries(res.data.logPosts as Record<string, any[]>).forEach(([key, value]) => {
        const initialArr = updateData[key] || [];
        updateData[key] = [...initialArr, ...value];
      });
      setTotalLogPostCount((p) => ({
        current:
          p.current +
          Object.values(res.data.logPosts).reduce<number>((prev, c: any) => {
            const newCount = prev + c.length;
            return newCount;
          }, 0),
        total: res.data.totalLogPostInTimePeriod,
      }));
      setLogPostData(updateData);
      nextCreatedAt.current = res.data.lastCreatedAt;
    } else {
      toast.error('Error getting Log Post data.');
    }

    setLoadingMore(false);
  };

  useEffect(() => {
    const changed = memoizedDateRange.current[0] !== dateRange[0] || memoizedDateRange.current[1] !== dateRange[1];
    memoizedDateRange.current = dateRange;
    if (((!dateRange[0] && !dateRange[1]) || (dateRange[0] && dateRange[1])) && changed) {
      getLogPostData({ newDateRange: dateRange });
    }
  }, [dateRange, getLogPostData]);

  if (loadingLogPostData) {
    return (
      <div className={styles.logPostLoaderContainer}>
        <img src={LoaderSvg} alt="loader" width={100} />
      </div>
    );
  }

  if (!Object.keys(logPostData).length) {
    return (
      <>
        <LogPostFtux onAddPostClick={() => setIsAddLogPostOpen(true)} />
        <AddLogPost
          open={isAddLogPostOpen}
          onClose={() => setIsAddLogPostOpen(false)}
          creatorDetails={creatorDetails}
          refetchLogPosts={() => getLogPostData({ newDateRange: dateRange })}
        />
      </>
    );
  }

  return (
    <div className={styles.logPostContainer}>
      <div className={styles.logPostContainerHeader}>
        <Button bgColor="#7F38E9" onClick={() => setIsAddLogPostOpen(true)}>
          <AddIcon width={28} height={28} />
          Add Post
        </Button>
        <div className={styles.logPostDatPickerDiv}>
          <CustomDatePicker
            selectedDays={selectedDays}
            showDatePicker={showDatePicker}
            dateRange={dateRange}
            setDateRange={setDateRange}
            setSelectedDays={setSelectedDays}
            setShowDatePicker={setShowDatePicker}
          />
        </div>
      </div>
      {Object.keys(logPostData).map((yearMonth) => (
        <LogPosts
          creatorDetails={creatorDetails}
          key={yearMonth}
          logPostData={logPostData[yearMonth]}
          yearMonth={yearMonth}
        />
      ))}
      {totalLogPostCount.total > 6 && totalLogPostCount.current < totalLogPostCount.total && (
        <div className={styles.loadMoreButtonContainer}>
          {loadingMore ? <Loader color="#7F38E9" size="sm" /> : <LoadMoreButton onClick={loadMoreLogPost} />}
        </div>
      )}
      <AddLogPost
        open={isAddLogPostOpen}
        onClose={() => setIsAddLogPostOpen(false)}
        creatorDetails={creatorDetails}
        refetchLogPosts={() => getLogPostData({ newDateRange: dateRange })}
      />
    </div>
  );
}

export default LogPostsContainer;
